import React, { useState } from "react";
import { assetRepo, resourceRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, Card, DataTable, Drawer, Input, Modal, PageHead, Select, StatusBadge, Tabs, Timeline, baht, formatDate, useAsync, useTableState } from "../../components/ui";

const ASSET_STATUS = ["available", "busy", "maintenance"];
const dateInput = (value) => (value ? String(value).slice(0, 10) : "");
const EMPTY_ASSET = { id: "", code: "", name: "", category: "", value: "", status: "available", branchId: "", purchasedAt: "", nextServiceAt: "", odometer: "" };
const EMPTY_LOG = { date: "", detail: "", vendor: "", cost: "", nextServiceAt: "", odometer: "" };
const EMPTY_RESOURCE = { id: "", name: "", type: "", branchId: "", capacity: "1", status: "available", isActive: true };

export default function Assets() {
  const { orgId, branchId, branches, businessModel, toast, hasModule } = useApp();
  const showResources = hasModule("resource");
  const [tab, setTab] = useState("assets");
  const [assetForm, setAssetForm] = useState(null);
  const [logForm, setLogForm] = useState(null);
  const [resourceForm, setResourceForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { tx } = useI18n();
  const tableState = useTableState();
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);

  const { data, loading, reload } = useAsync(
    () =>
      assetRepo.list(orgId, {
        page: tableState.page,
        perPage: tableState.perPage,
        search: tableState.search,
        filter: (row) => (!branchId || row.branchId === branchId) && (!status || row.status === status),
      }),
    [orgId, branchId, status, tableState.page, tableState.perPage, tableState.search]
  );

  const { data: resources, loading: resLoading, reload: reloadResources } = useAsync(
    () => (showResources ? resourceRepo.all(orgId, (row) => !branchId || row.branchId === branchId) : Promise.resolve([])),
    [orgId, branchId, showResources],
    []
  );
  const branchOptions = (branches || []).map((b) => ({ value: b.id, label: b.name }));
  const branchName = (id) => branches?.find((b) => b.id === id)?.name || "-";

  const run = async (job, done) => {
    setSaving(true);
    try {
      await job();
      done();
    } catch (error) {
      toast(error?.message || tx("บันทึกไม่สำเร็จ", "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const openAsset = (asset) =>
    setAssetForm(
      asset
        ? { ...EMPTY_ASSET, ...asset, value: String(asset.value ?? ""), purchasedAt: dateInput(asset.purchasedAt), nextServiceAt: dateInput(asset.nextServiceAt), odometer: asset.odometer === null || asset.odometer === undefined ? "" : String(asset.odometer) }
        : { ...EMPTY_ASSET, branchId: branchId || branches?.[0]?.id || "" }
    );

  const saveAsset = () =>
    run(async () => {
      const f = assetForm;
      if (!f.name.trim() || !f.code.trim()) throw new Error(tx("กรอกรหัสและชื่อทรัพย์สิน", "Code and name required"));
      const value = f.value === "" ? 0 : Number(f.value);
      if (!(value >= 0)) throw new Error(tx("มูลค่าต้องไม่ติดลบ", "Value must not be negative"));
      const duplicate = (await assetRepo.all(orgId, (row) => row.code === f.code.trim() && row.id !== f.id)).length > 0;
      if (duplicate) throw new Error(tx("รหัสทรัพย์สินนี้ถูกใช้แล้ว", "Asset code already used"));
      const payload = {
        code: f.code.trim(),
        name: f.name.trim(),
        category: f.category.trim(),
        value,
        status: f.status,
        branchId: f.branchId || branchId || branches?.[0]?.id || "",
        purchasedAt: f.purchasedAt || null,
        nextServiceAt: f.nextServiceAt || null,
        odometer: f.odometer === "" ? null : Number(f.odometer),
      };
      if (f.id) {
        const saved = await assetRepo.update(orgId, f.id, payload);
        setActive((prev) => (prev && prev.id === f.id ? { ...prev, ...payload, ...(saved || {}) } : prev));
      } else {
        await assetRepo.create(orgId, { ...payload, lastServiceAt: null, maintenanceLogs: [] });
      }
    }, () => {
      setAssetForm(null);
      reload();
      toast(tx("บันทึกทรัพย์สินแล้ว", "Asset saved"), "success");
    });

  const removeAsset = async (asset) => {
    if (!window.confirm(tx(`ลบทรัพย์สิน ${asset.name}? ประวัติซ่อมบำรุงจะหายไปด้วย`, `Delete ${asset.name}? Its maintenance history is removed too.`))) return;
    await run(() => assetRepo.remove(orgId, asset.id), () => {
      setActive(null);
      reload();
      toast(tx("ลบทรัพย์สินแล้ว", "Asset deleted"), "success");
    });
  };

  const saveLog = () =>
    run(async () => {
      const f = logForm;
      if (!f.date || !f.detail.trim()) throw new Error(tx("กรอกวันที่และรายละเอียดงานซ่อม", "Date and detail required"));
      const cost = f.cost === "" ? 0 : Number(f.cost);
      if (!(cost >= 0)) throw new Error(tx("ค่าใช้จ่ายต้องไม่ติดลบ", "Cost must not be negative"));
      // อ่านทรัพย์สินล่าสุดก่อนต่อ log กันทับของที่เพิ่งเพิ่ม
      const fresh = await assetRepo.get(orgId, active.id);
      if (!fresh || fresh.id !== active.id) throw new Error(tx("ไม่พบทรัพย์สิน", "Asset not found"));
      const log = { id: `mnt_${Date.now().toString(36)}`, date: f.date, detail: f.detail.trim(), vendor: f.vendor.trim(), cost };
      const maintenanceLogs = [log, ...(Array.isArray(fresh.maintenanceLogs) ? fresh.maintenanceLogs : [])];
      const patch = {
        maintenanceLogs,
        lastServiceAt: !fresh.lastServiceAt || f.date > dateInput(fresh.lastServiceAt) ? f.date : fresh.lastServiceAt,
        ...(f.nextServiceAt ? { nextServiceAt: f.nextServiceAt } : {}),
        ...(f.odometer !== "" ? { odometer: Number(f.odometer) } : {}),
      };
      await assetRepo.update(orgId, fresh.id, patch);
      setActive({ ...fresh, ...patch });
    }, () => {
      setLogForm(null);
      reload();
      toast(tx("บันทึกงานซ่อมบำรุงแล้ว", "Maintenance logged"), "success");
    });

  const saveResource = () =>
    run(async () => {
      const f = resourceForm;
      const capacity = Number(f.capacity);
      if (!f.name.trim()) throw new Error(tx("กรอกชื่อทรัพยากร", "Name required"));
      if (!(capacity >= 1) || !Number.isInteger(capacity)) throw new Error(tx("ความจุต้องเป็นจำนวนเต็มตั้งแต่ 1", "Capacity must be an integer >= 1"));
      const payload = {
        name: f.name.trim(),
        type: f.type.trim() || businessModel.resourceLabel,
        branchId: f.branchId || branchId || branches?.[0]?.id || "",
        capacity,
        status: f.status,
        isActive: !!f.isActive,
      };
      if (f.id) await resourceRepo.update(orgId, f.id, payload);
      else await resourceRepo.create(orgId, payload);
    }, () => {
      setResourceForm(null);
      reloadResources();
      toast(tx("บันทึกทรัพยากรแล้ว", "Resource saved"), "success");
    });

  const removeResource = async (row) => {
    if (!window.confirm(tx(`ลบ ${row.name}? นัดหมายที่ผูกอยู่จะไม่มีทรัพยากรแสดง`, `Delete ${row.name}?`))) return;
    await run(() => resourceRepo.remove(orgId, row.id), () => {
      reloadResources();
      toast(tx("ลบทรัพยากรแล้ว", "Resource deleted"), "success");
    });
  };

  return (
    <>
      <PageHead
        title={tx("ทะเบียนทรัพย์สิน", "Asset register")}
        subtitle={tx(`ทะเบียนคุม${businessModel.resourceLabel}พร้อมประวัติซ่อมบำรุง`, "Asset registry with maintenance history")}
        actions={
          tab === "assets" ? (
            <Button variant="primary" onClick={() => openAsset(null)}>+ {tx("เพิ่มทรัพย์สิน", "Add asset")}</Button>
          ) : (
            <Button variant="primary" onClick={() => setResourceForm({ ...EMPTY_RESOURCE, type: businessModel.resourceLabel, branchId: branchId || branches?.[0]?.id || "" })}>+ {tx("เพิ่มทรัพยากร", "Add resource")}</Button>
          )
        }
      />

      {showResources ? (
        <Tabs tabs={[{ key: "assets", label: tx("ทรัพย์สิน", "Assets") }, { key: "resources", label: tx(`ทรัพยากร (${businessModel.resourceLabel})`, `Resources (${businessModel.resourceLabel})`) }]} value={tab} onChange={setTab} />
      ) : null}

      {tab === "resources" && showResources ? (
        <DataTable
          loading={resLoading}
          rows={resources || []}
          emptyText={tx("ยังไม่มีทรัพยากร", "No resources")}
          columns={[
            { key: "name", label: tx("ชื่อ", "Name") },
            { key: "type", label: tx("ประเภท", "Type") },
            { key: "branchId", label: tx("สาขา", "Branch"), render: (row) => branchName(row.branchId) },
            { key: "capacity", label: tx("ความจุ", "Capacity"), align: "right" },
            { key: "status", label: tx("สถานะ", "Status"), render: (row) => (row.isActive === false ? <StatusBadge status="inactive" /> : <StatusBadge status={row.status} />) },
            {
              key: "action",
              label: "",
              render: (row) => (
                <>
                  <Button size="sm" onClick={() => setResourceForm({ ...EMPTY_RESOURCE, ...row, capacity: String(row.capacity ?? 1) })}>{tx("แก้ไข", "Edit")}</Button>{" "}
                  <Button size="sm" onClick={() => removeResource(row)}>{tx("ลบ", "Delete")}</Button>
                </>
              ),
            },
          ]}
        />
      ) : (
      <>

      <div className="filter-bar">
        <input className="input-crm" style={{ maxWidth: 260 }} placeholder={tx("ค้นหา", "Search")} value={tableState.search} onChange={(e) => tableState.setSearch(e.target.value)} />
        <Select options={["available", "busy", "maintenance"]} value={status} onChange={(e) => setStatus(e.target.value)} placeholder={tx("ทุกสถานะ", "All status")} style={{ width: "auto" }} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        onRowClick={setActive}
        emptyText={tx("ยังไม่มีทรัพย์สิน", "No assets")}
        columns={[
          { key: "code", label: tx("รหัส", "Code") },
          { key: "name", label: tx("ชื่อ", "Name") },
          { key: "category", label: tx("หมวดหมู่", "Category"), render: (row) => <Badge tone="indigo">{row.category}</Badge> },
          { key: "value", label: tx("มูลค่า", "Value"), align: "right", render: (row) => baht(row.value, 0) },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
          { key: "nextServiceAt", label: tx("ซ่อมบำรุงถัดไป", "Next service"), render: (row) => formatDate(row.nextServiceAt) },
        ]}
      />
      </>
      )}

      <Drawer
        open={!!active}
        title={active?.name}
        onClose={() => setActive(null)}
        footer={
          active ? (
            <>
              <Button onClick={() => removeAsset(active)}>{tx("ลบ", "Delete")}</Button>
              <Button onClick={() => openAsset(active)}>{tx("แก้ไข", "Edit")}</Button>
              <Button variant="primary" onClick={() => setLogForm({ ...EMPTY_LOG, date: new Date().toISOString().slice(0, 10) })}>{tx("บันทึกงานซ่อมบำรุง", "Log maintenance")}</Button>
            </>
          ) : null
        }
      >
        {active ? (
          <>
            <Card>
              <div className="u-between u-mb-8"><span className="u-muted u-fs-13">{tx("รหัส", "Code")}</span><span>{active.code}</span></div>
              <div className="u-between u-mb-8"><span className="u-muted u-fs-13">{tx("มูลค่า", "Value")}</span><span className="u-strong">{baht(active.value, 0)}</span></div>
              {active.odometer !== null && active.odometer !== undefined ? <div className="u-between u-mb-8"><span className="u-muted u-fs-13">{tx("เลขไมล์", "Odometer")}</span><span>{active.odometer?.toLocaleString()} km</span></div> : null}
              <div className="u-between u-mb-8"><span className="u-muted u-fs-13">{tx("ซื้อเมื่อ", "Purchased")}</span><span>{formatDate(active.purchasedAt)}</span></div>
              <div className="u-between"><span className="u-muted u-fs-13">{tx("สถานะ", "Status")}</span><StatusBadge status={active.status} /></div>
            </Card>
            <Card title={tx("ประวัติซ่อมบำรุง", "Maintenance history")}>
              <Timeline items={(active.maintenanceLogs || []).map((log) => ({ id: log.id, title: log.detail, detail: log.vendor, amount: log.cost, createdAt: log.date }))} />
            </Card>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={!!assetForm}
        title={assetForm?.id ? tx("แก้ไขทรัพย์สิน", "Edit asset") : tx("เพิ่มทรัพย์สิน", "New asset")}
        onClose={() => setAssetForm(null)}
        footer={<><Button onClick={() => setAssetForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveAsset}>{tx("บันทึก", "Save")}</Button></>}
      >
        {assetForm ? (
          <>
            <Input label={tx("รหัส", "Code")} value={assetForm.code} onChange={(e) => setAssetForm({ ...assetForm, code: e.target.value })} />
            <Input label={tx("ชื่อ", "Name")} value={assetForm.name} onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })} />
            <Input label={tx("หมวดหมู่", "Category")} value={assetForm.category} onChange={(e) => setAssetForm({ ...assetForm, category: e.target.value })} />
            <Input label={tx("มูลค่า (บาท)", "Value (THB)")} type="number" min="0" value={assetForm.value} onChange={(e) => setAssetForm({ ...assetForm, value: e.target.value })} />
            <Select label={tx("สถานะ", "Status")} options={ASSET_STATUS} value={assetForm.status} onChange={(e) => setAssetForm({ ...assetForm, status: e.target.value })} />
            <Select label={tx("สาขา", "Branch")} options={branchOptions} value={assetForm.branchId} onChange={(e) => setAssetForm({ ...assetForm, branchId: e.target.value })} />
            <Input label={tx("วันที่ซื้อ", "Purchased")} type="date" value={assetForm.purchasedAt} onChange={(e) => setAssetForm({ ...assetForm, purchasedAt: e.target.value })} />
            <Input label={tx("ซ่อมบำรุงถัดไป", "Next service")} type="date" value={assetForm.nextServiceAt} onChange={(e) => setAssetForm({ ...assetForm, nextServiceAt: e.target.value })} />
            <Input label={tx("เลขไมล์ (km) — เว้นว่างถ้าไม่เกี่ยวข้อง", "Odometer (km) — leave blank if n/a")} type="number" min="0" value={assetForm.odometer} onChange={(e) => setAssetForm({ ...assetForm, odometer: e.target.value })} />
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!logForm}
        title={tx("บันทึกงานซ่อมบำรุง", "Log maintenance")}
        onClose={() => setLogForm(null)}
        footer={<><Button onClick={() => setLogForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveLog}>{tx("บันทึก", "Save")}</Button></>}
      >
        {logForm ? (
          <>
            <Input label={tx("วันที่ซ่อม", "Date")} type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} />
            <Input label={tx("รายละเอียด", "Detail")} value={logForm.detail} onChange={(e) => setLogForm({ ...logForm, detail: e.target.value })} />
            <Input label={tx("ผู้ให้บริการ / ช่าง", "Vendor")} value={logForm.vendor} onChange={(e) => setLogForm({ ...logForm, vendor: e.target.value })} />
            <Input label={tx("ค่าใช้จ่าย (บาท)", "Cost (THB)")} type="number" min="0" value={logForm.cost} onChange={(e) => setLogForm({ ...logForm, cost: e.target.value })} />
            <Input label={tx("ซ่อมบำรุงครั้งถัดไป (ไม่บังคับ)", "Next service (optional)")} type="date" value={logForm.nextServiceAt} onChange={(e) => setLogForm({ ...logForm, nextServiceAt: e.target.value })} />
            {active?.odometer !== null && active?.odometer !== undefined ? (
              <Input label={tx("เลขไมล์ปัจจุบัน (km)", "Current odometer (km)")} type="number" min="0" value={logForm.odometer} onChange={(e) => setLogForm({ ...logForm, odometer: e.target.value })} />
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!resourceForm}
        title={resourceForm?.id ? tx("แก้ไขทรัพยากร", "Edit resource") : tx("เพิ่มทรัพยากร", "New resource")}
        onClose={() => setResourceForm(null)}
        footer={<><Button onClick={() => setResourceForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveResource}>{tx("บันทึก", "Save")}</Button></>}
      >
        {resourceForm ? (
          <>
            <Input label={tx("ชื่อ (เช่น ห้อง 1 / เตียง A / ช่างสมชาย)", "Name")} value={resourceForm.name} onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })} />
            <Input label={tx("ประเภท", "Type")} value={resourceForm.type} onChange={(e) => setResourceForm({ ...resourceForm, type: e.target.value })} />
            <Select label={tx("สาขา", "Branch")} options={branchOptions} value={resourceForm.branchId} onChange={(e) => setResourceForm({ ...resourceForm, branchId: e.target.value })} />
            <Input label={tx("ความจุ (จำนวนคนที่รองรับพร้อมกัน)", "Capacity")} type="number" min="1" value={resourceForm.capacity} onChange={(e) => setResourceForm({ ...resourceForm, capacity: e.target.value })} />
            <Select label={tx("สถานะ", "Status")} options={ASSET_STATUS} value={resourceForm.status} onChange={(e) => setResourceForm({ ...resourceForm, status: e.target.value })} />
            <Select label={tx("เปิดใช้งาน", "Active")} options={[{ value: "1", label: tx("เปิดใช้งาน", "Active") }, { value: "0", label: tx("ปิดใช้งาน", "Inactive") }]} value={resourceForm.isActive === false ? "0" : "1"} onChange={(e) => setResourceForm({ ...resourceForm, isActive: e.target.value === "1" })} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
